import { format, isSameDay } from "date-fns";
import { Clock, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { DateAvailability } from "./types";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EditAvailabilityModal from "./EditAvailabilityModal";

interface SelectedDatesListProps {
  availability: DateAvailability[];
  onUpdateAvailability: (updated: DateAvailability) => void;
  onRemoveDate: (date: Date) => void;
  setEditingDate: React.Dispatch<React.SetStateAction<DateAvailability | null>>;
}

const SelectedDatesList = ({
  availability,
  onUpdateAvailability,
  setEditingDate,
  onRemoveDate,
}: SelectedDatesListProps) => {
  if (availability?.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
        Select dates from the calendar above to set your availability
      </div>
    );
  }

  const getSlotsSummary = (dateAvail: DateAvailability) => {
    if (dateAvail.isAllDay) return "Available All Day";
    if (dateAvail.timeSlots.length === 0) return "No slots selected";
    return dateAvail.timeSlots.map((s) => `${s.start} - ${s.end}`).join(", ");
  };

  return (
    <>
      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
        <AnimatePresence mode="popLayout">
          {availability.map((dateAvail) => {
            const dateObject = new Date(dateAvail.date);

            return (
              <motion.div
                key={dateObject.toISOString()}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="border border-border rounded-lg overflow-hidden bg-card"
              >
                <div className="flex items-center justify-between p-3 bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-accent" />
                    <span className="text-sm font-medium text-foreground">
                      {format(dateObject, "EEE, do MMM")}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingDate(dateAvail)}
                      className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => onRemoveDate(dateObject)}
                      className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                  {getSlotsSummary(dateAvail)}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </>
  );
};

export default SelectedDatesList;

// import { format, isSameDay } from "date-fns";
// import { Check, Clock, Pencil, Trash2, X } from "lucide-react";
// import { cn } from "@/lib/utils";
// import { DateAvailability, TimeSlot, DEFAULT_TIME_SLOTS } from "./types";
// import { useState } from "react";
// import { motion, AnimatePresence } from "framer-motion";

// interface SelectedDatesListProps {
//   availability: DateAvailability[];
//   onToggleAllDay: (date: Date) => void;
//   onToggleTimeSlot: (date: Date, slot: TimeSlot) => void;
//   onRemoveDate: (date: Date) => void;
// }

// const SelectedDatesList = ({
//   availability,
//   onToggleAllDay,
//   onToggleTimeSlot,
//   onRemoveDate,
// }: SelectedDatesListProps) => {
//   const [editingDate, setEditingDate] = useState<Date | null>(null);
//   console.log(availability);
//   if (availability?.length === 0) {
//     return (
//       <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
//         Select dates from the calendar above to set your availability
//       </div>
//     );
//   }

//   const isSlotSelected = (dateAvail: DateAvailability, slot: TimeSlot) =>
//     dateAvail.timeSlots.some((s) => s.id === slot.id);

//   const getSlotsSummary = (dateAvail: DateAvailability) => {
//     if (dateAvail.isAllDay) return "Available All Day";
//     if (dateAvail.timeSlots.length === 0) return "No slots selected";
//     return dateAvail.timeSlots.map((s) => `${s.start} - ${s.end}`).join(", ");
//   };

//   return (
//     <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
//       <AnimatePresence mode="popLayout">
//         {availability?.map((dateAvail) => {
//           const isEditing =
//             editingDate && isSameDay(editingDate, dateAvail.date);

//           const dateObject = new Date(dateAvail.date); // Convert string to Date object

//           return (
//             <motion.div
//               key={dateObject.toISOString()} // Use ISO string of the Date object
//               initial={{ opacity: 0, y: -10 }}
//               animate={{ opacity: 1, y: 0 }}
//               exit={{ opacity: 0, x: -20 }}
//               transition={{ duration: 0.2 }}
//               className="border border-border rounded-lg overflow-hidden bg-card"
//             >
//               {/* Date Header */}
//               <div className="flex items-center justify-between p-3 bg-secondary/30">
//                 <div className="flex items-center gap-2">
//                   <Clock className="w-4 h-4 text-accent" />
//                   <span className="text-sm font-medium text-foreground">
//                     {format(dateObject, "EEE, do MMM")}
//                   </span>
//                 </div>
//                 <div className="flex items-center gap-1">
//                   <button
//                     onClick={() =>
//                       setEditingDate(isEditing ? null : dateObject)
//                     }
//                     className={cn(
//                       "p-1.5 rounded-md transition-colors",
//                       isEditing
//                         ? "bg-accent text-accent-foreground"
//                         : "hover:bg-secondary text-muted-foreground hover:text-foreground",
//                     )}
//                     aria-label={isEditing ? "Close edit" : "Edit time slots"}
//                   >
//                     {isEditing ? (
//                       <X className="w-4 h-4" />
//                     ) : (
//                       <Pencil className="w-4 h-4" />
//                     )}
//                   </button>
//                   <button
//                     onClick={() => onRemoveDate(dateObject)}
//                     className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
//                     aria-label="Remove date"
//                   >
//                     <Trash2 className="w-4 h-4" />
//                   </button>
//                 </div>
//               </div>

//               {/* Slots Summary or Edit Mode */}
//               <AnimatePresence mode="wait">
//                 {isEditing ? (
//                   <motion.div
//                     key="edit"
//                     initial={{ height: 0, opacity: 0 }}
//                     animate={{ height: "auto", opacity: 1 }}
//                     exit={{ height: 0, opacity: 0 }}
//                     transition={{ duration: 0.2 }}
//                     className="p-3 space-y-3 border-t border-border"
//                   >
//                     {/* All Day Toggle */}
//                     <button
//                       onClick={() => onToggleAllDay(dateObject)}
//                       className={cn(
//                         "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border",
//                         dateAvail.isAllDay
//                           ? "bg-success/10 text-success border-success/30"
//                           : "text-muted-foreground hover:bg-secondary border-border",
//                       )}
//                     >
//                       <div
//                         className={cn(
//                           "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
//                           dateAvail.isAllDay
//                             ? "bg-success border-success"
//                             : "border-border bg-card",
//                         )}
//                       >
//                         {dateAvail.isAllDay && (
//                           <Check className="w-3 h-3 text-success-foreground" />
//                         )}
//                       </div>
//                       Available All Day
//                     </button>

//                     {/* Time Slots */}
//                     {!dateAvail.isAllDay && (
//                       <div className="grid grid-cols-2 gap-2">
//                         {DEFAULT_TIME_SLOTS.map((slot) => {
//                           const selected = isSlotSelected(dateAvail, slot);
//                           return (
//                             <button
//                               key={slot.id}
//                               onClick={() => onToggleTimeSlot(dateObject, slot)}
//                               className={cn(
//                                 "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border",
//                                 selected
//                                   ? "bg-primary text-primary-foreground border-primary"
//                                   : "bg-card text-foreground border-border hover:border-primary/50",
//                               )}
//                             >
//                               <span>{slot.start}</span>
//                               <span className="opacity-60">-</span>
//                               <span>{slot.end}</span>
//                             </button>
//                           );
//                         })}
//                       </div>
//                     )}
//                   </motion.div>
//                 ) : (
//                   <motion.div
//                     key="summary"
//                     initial={{ opacity: 0 }}
//                     animate={{ opacity: 1 }}
//                     exit={{ opacity: 0 }}
//                     className="px-3 py-2 text-xs text-muted-foreground border-t border-border"
//                   >
//                     {getSlotsSummary(dateAvail)}
//                   </motion.div>
//                 )}
//               </AnimatePresence>
//             </motion.div>
//           );
//         })}
//       </AnimatePresence>
//     </div>
//   );
// };

// export default SelectedDatesList;
